import { useEffect, useState } from "react";
import { api, formatApiError, fileUrl } from "../lib/api";
import { PageHeader, Card, EmptyState } from "../components/common";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Cube, Trash, UploadSimple, DownloadSimple } from "@phosphor-icons/react";

export default function Bim() {
  const { user } = useAuth();
  const canDelete = ["admin", "gerente_general", "gerente_area"].includes(user?.role) || !!user?.can_delete;
  const isManager = user?.role !== "contratista";
  const [models, setModels] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);

  const load = () => api.get("/bim").then((r) => setModels(r.data));
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    if (!/\.(ifc|rvt|nwd|nwc|rfa|rte|dwg|dwfx)$/i.test(file.name)) return toast.error("Sube un archivo BIM (.rvt, .nwd, .ifc, .nwc, .rfa, .dwg)");
    setUploading(true); setUploadPct(0);
    try {
      const CHUNK = 5 * 1024 * 1024;
      const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const total = Math.ceil(file.size / CHUNK) || 1;
      for (let i = 0; i < total; i++) {
        const blob = file.slice(i * CHUNK, (i + 1) * CHUNK);
        const cfd = new FormData();
        cfd.append("upload_id", uploadId);
        cfd.append("index", i);
        cfd.append("chunk", blob, "chunk.part");
        await api.post("/bim/chunk", cfd);
        setUploadPct(Math.round(((i + 1) * 100) / total));
      }
      const done = new FormData();
      done.append("upload_id", uploadId);
      done.append("filename", file.name);
      done.append("total", total);
      done.append("name", file.name.replace(/\.[^.]+$/, ""));
      await api.post("/bim/chunk/complete", done);
      toast.success("Archivo BIM almacenado"); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "No se pudo subir el archivo"); }
    finally { setUploading(false); setUploadPct(0); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este archivo BIM?")) return;
    await api.delete(`/bim/${id}`); toast.success("Eliminado"); load();
  };

  return (
    <div>
      <PageHeader overline="Almacén de archivos" title="BIM"
        action={isManager && (
          <label className="inline-flex items-center gap-2 h-11 px-4 bg-primary text-white rounded-sm font-semibold text-sm cursor-pointer hover:bg-orange-600 transition-colors" data-testid="bim-upload-btn">
            <UploadSimple size={18} weight="bold" /> {uploading ? `Subiendo… ${uploadPct}%` : "Subir archivo"}
            <input type="file" accept=".ifc,.rvt,.nwd,.nwc,.rfa,.rte,.dwg,.dwfx" className="hidden" onChange={(e) => upload(e.target.files[0])} disabled={uploading} />
          </label>
        )} />

      <Card className="p-4 mb-5 bg-blue-50 border-blue-200">
        <p className="text-sm text-blue-900">Este es un <b>almacén de archivos BIM</b>. Sube tus modelos (.rvt, .nwd, .ifc, etc.) para tenerlos centralizados y <b>descargarlos</b> cuando los necesites en tu plataforma o software BIM. Aquí no se visualizan en 3D.</p>
      </Card>

      {models.length === 0 ? (
        <EmptyState icon={Cube} title="Sin archivos BIM" subtitle="Sube tu primer archivo (.rvt, .ifc, .nwd) para tenerlo centralizado y compartirlo con el equipo." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {models.map((m) => (
            <Card key={m.id} className="p-5 hover:-translate-y-0.5 hover:shadow-md transition-transform duration-200" data-testid={`bim-card-${m.id}`}>
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="w-11 h-11 rounded-sm bg-primary/10 flex items-center justify-center mb-3"><Cube size={22} weight="fill" className="text-primary" /></div>
                  <div className="font-heading font-bold truncate">{m.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{m.file_name}</div>
                  <div className="text-xs text-muted-foreground mt-1">{new Date(m.created_at).toLocaleDateString("es-MX")} · {m.uploaded_by}</div>
                </div>
                {canDelete && <button onClick={() => remove(m.id)} className="p-1.5 text-muted-foreground hover:text-danger" data-testid={`bim-delete-${m.id}`}><Trash size={18} /></button>}
              </div>
              <a href={fileUrl(m.file_path)} target="_blank" rel="noreferrer" download={m.file_name || true} data-testid={`bim-download-${m.id}`} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"><DownloadSimple size={16} weight="bold" /> Descargar archivo</a>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
